// PredictiveMaintenanceDashboard.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Grid,
  Circle,
  VStack,
  HStack,
  Heading,
  Badge,
  Button,
  Progress,
  Dialog,
  Input,
  Select,
  Textarea,
  NumberInput,
  type BoxProps,
} from "@chakra-ui/react";
import { toaster } from "@/components/ui/toaster";
import MaintenanceHistoryModal from "@/components/Dashboard/MaintenanceHistoryModal";

// 정비 내역 타입 정의
interface MaintenanceRecord {
  id: string;
  equipmentId: number;
  equipmentName: string;
  date: string;
  code: string;
  description: string;
  cost: number;
  partsUsed: string;
  partsDuration: string;
  technician: string;
  priority: "high" | "medium" | "low";
  status: "scheduled" | "in-progress" | "completed";
  estimatedHours: number;
  actualHours?: number;
}

// 커스텀 카드 컴포넌트
const CustomCard = ({
  children,
  ...props
}: BoxProps & { children?: React.ReactNode }) => (
  <Box
    bg="white"
    border="1px"
    borderColor="gray.200"
    borderRadius="md"
    p={4}
    shadow="md"
    {...props}
  >
    {children}
  </Box>
);

// 바 차트 데이터 타입
interface BarChartDataItem {
  month: string;
  [key: string]: string | number;
}

// 간단한 바 차트 컴포넌트
const SimpleBarChart = ({
  data,
  dataKey,
  color = "#3182ce",
}: {
  data: BarChartDataItem[];
  dataKey: string;
  color?: string;
}) => (
  <Box>
    <VStack gap={2} align="stretch">
      {data.map((item) => (
        <HStack key={item.month} justify="space-between">
          <Text fontSize="sm" minW="40px">
            {item.month}
          </Text>
          <HStack flex={1} gap={2}>
            <Box
              w={`${((item[dataKey] as number) / Math.max(...data.map((d) => d[dataKey] as number))) * 100}%`}
              h="20px"
              bg={color}
              borderRadius="sm"
              minW="10px"
            />
            <Text fontSize="sm" fontWeight="medium" minW="60px">
              ₩{item[dataKey]}만
            </Text>
          </HStack>
        </HStack>
      ))}
    </VStack>
  </Box>
);

// 도넛 차트 데이터 타입
interface DonutChartDataItem {
  name: string;
  value: number;
  color: string;
}

// 간단한 도넛 차트 컴포넌트
const SimpleDonutChart = ({ data }: { data: DonutChartDataItem[] }) => (
  <VStack gap={3}>
    {data.map((item, index) => (
      <HStack
        key={`${item.name}-${item.value}-${index}`}
        justify="space-between"
        w="100%"
        bgColor="white"
        p={3}
      >
        <HStack>
          <Box w={4} h={4} bg={item.color} borderRadius="sm" />
          <Text fontSize="md">{item.name}</Text>
        </HStack>
        <Text fontSize="md" fontWeight="bold">
          {item.value}대
        </Text>
      </HStack>
    ))}
  </VStack>
);

// 간단한 라인 차트 컴포넌트
const SimpleLineChart = ({
  data,
  dataKey,
  color = "#3182ce",
}: {
  data: BarChartDataItem[];
  dataKey: string;
  color?: string;
}) => {
  const maxValue = Math.max(...data.map((d) => d[dataKey] as number));
  const chartHeight = 80;

  return (
    <Box position="relative" h="150px" p={4} bgColor="white">
      <HStack h="100%" align="end" gap={1}>
        {data.map((item, index) => {
          const barHeight =
            ((item[dataKey] as number) / maxValue) * chartHeight;

          return (
            <VStack
              key={`${item.month}-${index}`}
              gap={1}
              flex={1}
              align="center"
            >
              <Text fontSize="xs" color="gray.500">
                {item[dataKey]}%
              </Text>

              <Box
                h={`${chartHeight}px`}
                w="100%"
                display="flex"
                alignItems="end"
                justifyContent="center"
              >
                <Box
                  bg={color}
                  w="80%"
                  h={`${barHeight}px`}
                  borderRadius="md"
                  minH="4px"
                />
              </Box>

              <Text fontSize="xs" color="gray.400">
                {item.month}
              </Text>
            </VStack>
          );
        })}
      </HStack>
    </Box>
  );
};

// 커스텀 Progress 컴포넌트
const CustomProgress = ({
  value,
  max = 100,
  colorScheme = "blue",
  size = "md",
}: {
  value: number;
  max?: number;
  colorScheme?: "red" | "orange" | "green" | "blue" | "purple";
  size?: "sm" | "md" | "lg";
}) => {
  const getColorScheme = (
    scheme: "red" | "orange" | "green" | "blue" | "purple"
  ) => {
    switch (scheme) {
      case "red":
        return "#e53e3e";
      case "orange":
        return "#dd6b20";
      case "green":
        return "#38a169";
      case "blue":
        return "#3182ce";
      case "purple":
        return "#805ad5";
      default:
        return "#3182ce";
    }
  };

  const getHeight = (size: "sm" | "md" | "lg") => {
    switch (size) {
      case "sm":
        return "4px";
      case "md":
        return "8px";
      case "lg":
        return "12px";
      default:
        return "8px";
    }
  };

  return (
    <Progress.Root value={value} max={max} w="100%">
      <Progress.Track bg="gray.200" borderRadius="md" h={getHeight(size)}>
        <Progress.Range bg={getColorScheme(colorScheme)} borderRadius="md" />
      </Progress.Track>
    </Progress.Root>
  );
};

// 폼 컨트롤 컴포넌트
const FormControl = ({
  children,
  isRequired = false,
  ...props
}: BoxProps & { children?: React.ReactNode; isRequired?: boolean }) => (
  <Box {...props}>{children}</Box>
);

const FormLabel = ({
  children,
  ...props
}: {
  children?: React.ReactNode;
  isRequired?: boolean;
} & React.ComponentProps<typeof Text>) => (
  <Text fontSize="sm" fontWeight="medium" mb={1} {...props}>
    {children}
    {props.isRequired && (
      <Text as="span" color="red.500" ml={1}>
        *
      </Text>
    )}
  </Text>
);

// 정비 스케줄 모달 컴포넌트
import { createListCollection } from "@chakra-ui/react";

const MaintenanceScheduleModal = ({
  isOpen,
  onClose,
  equipmentData,
  onSave,
}: {
  isOpen: boolean;
  onClose: () => void;
  equipmentData: any;
  onSave: (record: MaintenanceRecord) => void;
}) => {
  const [formData, setFormData] = useState({
    date: "",
    code: "",
    description: "",
    cost: 0,
    partsUsed: "",
    partsDuration: "",
    technician: "",
    priority: "medium" as "high" | "medium" | "low",
    estimatedHours: 0,
  });

  // 컬러 팔레트 정의
  const colorPalette = {
    PM: "#3182ce", // 파란색 - Preventive Maintenance
    CM: "#e53e3e", // 빨간색 - Corrective Maintenance
    PV: "#38a169", // 초록색 - Predictive Maintenance
    UP: "#805ad5", // 보라색 - Upgrade
    CL: "#dd6b20", // 주황색 - Cleaning
    IN: "#d69e2e", // 노란색 - Inspection
  } as const;

  type ColorPaletteKey = keyof typeof colorPalette;

  // 첫 두 글자로 컬러 코드를 가져오는 함수
  const getColorByPrefix = (value: string) => {
    const prefix = value.substring(0, 2).toUpperCase() as ColorPaletteKey;
    return colorPalette[prefix] || "#718096"; // 기본 회색
  };

  const workCodeCollection = createListCollection({
    items: [
      {
        label: "PM-001 (정기 윤활 및 그리스 보충)",
        value: "PM-001",
        color: getColorByPrefix("PM-001"),
      },
      {
        label: "PM-002 (벨트 장력 점검 및 조정)",
        value: "PM-002",
        color: getColorByPrefix("PM-002"),
      },
      {
        label: "PM-003 (필터 교체 및 청소)",
        value: "PM-003",
        color: getColorByPrefix("PM-003"),
      },
      {
        label: "PM-004 (베어링 점검 및 교체)",
        value: "PM-004",
        color: getColorByPrefix("PM-004"),
      },
      {
        label: "PM-005 (냉각수 교체 및 보충)",
        value: "PM-005",
        color: getColorByPrefix("PM-005"),
      },
      {
        label: "CM-001 (모터 고장 수리)",
        value: "CM-001",
        color: getColorByPrefix("CM-001"),
      },
      {
        label: "CM-002 (유압 시스템 누유 수리)",
        value: "CM-002",
        color: getColorByPrefix("CM-002"),
      },
      {
        label: "CM-003 (전기 회로 단락 수리)",
        value: "CM-003",
        color: getColorByPrefix("CM-003"),
      },
      {
        label: "CM-004 (기어박스 이상음 수리)",
        value: "CM-004",
        color: getColorByPrefix("CM-004"),
      },
      {
        label: "CM-005 (센서 오작동 수리)",
        value: "CM-005",
        color: getColorByPrefix("CM-005"),
      },
      {
        label: "PV-001 (진동 분석 및 밸런싱)",
        value: "PV-001",
        color: getColorByPrefix("PV-001"),
      },
      {
        label: "PV-002 (온도 모니터링 및 조치)",
        value: "PV-002",
        color: getColorByPrefix("PV-002"),
      },
      {
        label: "PV-003 (압력 측정 및 조정)",
        value: "PV-003",
        color: getColorByPrefix("PV-003"),
      },
      {
        label: "PV-004 (정렬 상태 점검)",
        value: "PV-004",
        color: getColorByPrefix("PV-004"),
      },
      {
        label: "UP-001 (CNC 소프트웨어 업데이트)",
        value: "UP-001",
        color: getColorByPrefix("UP-001"),
      },
      {
        label: "UP-002 (안전장치 추가 설치)",
        value: "UP-002",
        color: getColorByPrefix("UP-002"),
      },
      {
        label: "UP-003 (자동화 시스템 개선)",
        value: "UP-003",
        color: getColorByPrefix("UP-003"),
      },
      {
        label: "CL-001 (칩 제거 및 청소)",
        value: "CL-001",
        color: getColorByPrefix("CL-001"),
      },
      {
        label: "CL-002 (작업대 정리 및 청소)",
        value: "CL-002",
        color: getColorByPrefix("CL-002"),
      },
      {
        label: "CL-003 (냉각액 라인 청소)",
        value: "CL-003",
        color: getColorByPrefix("CL-003"),
      },
      {
        label: "IN-001 (정밀 측정 및 검사)",
        value: "IN-001",
        color: getColorByPrefix("IN-001"),
      },
      {
        label: "IN-002 (토크 값 재설정)",
        value: "IN-002",
        color: getColorByPrefix("IN-002"),
      },
      {
        label: "IN-003 (캘리브레이션 작업)",
        value: "IN-003",
        color: getColorByPrefix("IN-003"),
      },
    ],
  });

  const technicianCollection = createListCollection({
    items: [
      { label: "서동욱", value: "서동욱" },
      { label: "조현건", value: "조현건" },
      { label: "박성모", value: "박성모" },
      { label: "김민지", value: "김민지" },
      { label: "오상엽", value: "오상엽" },
      { label: "공영덕", value: "공영덕" },
      { label: "이정훈", value: "이정훈" },
      { label: "최영수", value: "최영수" },
      { label: "한지원", value: "한지원" },
      { label: "윤태호", value: "윤태호" },
      { label: "강미영", value: "강미영" },
      { label: "노준혁", value: "노준혁" },
      { label: "송기락", value: "송기락" },
      { label: "임수진", value: "임수진" },
      { label: "정우철", value: "정우철" },
    ],
  });

  const priorityCollection = createListCollection({
    items: [
      { label: "낮음", value: "low" },
      { label: "보통", value: "medium" },
      { label: "높음", value: "high" },
    ],
  });

  const handleSubmit = () => {
    if (!formData.date || !formData.code || !formData.description) {
      toaster.create({
        title: "필수 정보 누락",
        description: "날짜, 코드, 작업 내용을 입력해주세요.",
        type: "error",
        duration: 3000,
      });
      return;
    }

    const newRecord: MaintenanceRecord = {
      id: Date.now().toString(),
      equipmentId: equipmentData.equipmentId,
      equipmentName: equipmentData.equipmentName,
      date: formData.date,
      code: formData.code,
      description: formData.description,
      cost: formData.cost,
      partsUsed: formData.partsUsed,
      partsDuration: formData.partsDuration,
      technician: formData.technician,
      priority: formData.priority,
      status: "scheduled",
      estimatedHours: formData.estimatedHours,
    };

    onSave(newRecord);

    toaster.create({
      title: "정비 스케줄 등록 완료",
      description: "새로운 정비 일정이 등록되었습니다.",
      type: "success",
      duration: 3000,
    });

    // 폼 초기화
    setFormData({
      date: "",
      code: "",
      description: "",
      cost: 0,
      partsUsed: "",
      partsDuration: "",
      technician: "",
      priority: "medium",
      estimatedHours: 0,
    });

    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxW="2xl" mx={4}>
          <Dialog.Header>
            <Dialog.Title>
              정비 스케줄 등록 - {equipmentData?.equipmentName}
            </Dialog.Title>
            <Dialog.CloseTrigger />
          </Dialog.Header>

          <Dialog.Body>
            <VStack gap={4} align="stretch">
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl isRequired>
                  <FormLabel isRequired>정비 날짜</FormLabel>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel isRequired>작업 코드</FormLabel>
                  <Select.Root
                    collection={workCodeCollection}
                    value={[formData.code]}
                    onValueChange={(details) =>
                      setFormData({ ...formData, code: details.value[0] })
                    }
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="작업 코드 선택" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {workCodeCollection.items.map((item) => (
                          <Select.Item
                            key={item.value}
                            item={item}
                            style={{
                              borderLeft: `4px solid ${item.color}`,
                              paddingLeft: "8px",
                            }}
                          >
                            <HStack>
                              <Box
                                w={3}
                                h={3}
                                bg={item.color}
                                borderRadius="full"
                              />
                              <Text>{item.label}</Text>
                            </HStack>
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </FormControl>
              </Grid>

              <FormControl isRequired>
                <FormLabel isRequired>작업 내용</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="상세 작업 내용을 입력하세요"
                />
              </FormControl>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl>
                  <FormLabel>예상 비용 (만원)</FormLabel>
                  <NumberInput.Root
                    value={formData.cost.toString()}
                    onValueChange={(details) =>
                      setFormData({
                        ...formData,
                        cost: parseInt(details.value) || 0,
                      })
                    }
                    min={0}
                  >
                    <NumberInput.Control />
                    <NumberInput.Input />
                  </NumberInput.Root>
                </FormControl>

                <FormControl>
                  <FormLabel>예상 소요시간 (시간)</FormLabel>
                  <NumberInput.Root
                    value={formData.estimatedHours.toString()}
                    onValueChange={(details) =>
                      setFormData({
                        ...formData,
                        estimatedHours: parseInt(details.value) || 0,
                      })
                    }
                    min={0}
                  >
                    <NumberInput.Control />
                    <NumberInput.Input />
                  </NumberInput.Root>
                </FormControl>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl>
                  <FormLabel>사용 부품</FormLabel>
                  <Input
                    value={formData.partsUsed}
                    onChange={(e) =>
                      setFormData({ ...formData, partsUsed: e.target.value })
                    }
                    placeholder="예: 베어링, 오일필터"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>부품 사용 기간</FormLabel>
                  <Input
                    value={formData.partsDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        partsDuration: e.target.value,
                      })
                    }
                    placeholder="예: 6개월, 1년"
                  />
                </FormControl>
              </Grid>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl>
                  <FormLabel>담당 기술자</FormLabel>
                  <Select.Root
                    collection={technicianCollection}
                    value={[formData.technician]}
                    onValueChange={(details) =>
                      setFormData({ ...formData, technician: details.value[0] })
                    }
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="기술자 선택" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {technicianCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </FormControl>

                <FormControl>
                  <FormLabel>우선순위</FormLabel>
                  <Select.Root
                    collection={priorityCollection}
                    value={[formData.priority]}
                    onValueChange={(details) =>
                      setFormData({
                        ...formData,
                        priority: details.value[0] as "high" | "medium" | "low",
                      })
                    }
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="우선순위 선택" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                        <Select.ClearTrigger />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {priorityCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </FormControl>
              </Grid>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <HStack justify="flex-end" gap={3}>
              <Button variant="outline" onClick={onClose}>
                취소
              </Button>
              <Button colorScheme="blue" onClick={handleSubmit}>
                등록하기
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

// 정비 내역 조회 모달
// const MaintenanceHistoryModal = ({
//   isOpen,
//   onClose,
//   records
// }: {
//   isOpen: boolean
//   onClose: () => void
//   records: MaintenanceRecord[]
// }) => {
//   const getStatusColor = (status: string) => {
//     switch(status) {
//       case 'completed': return 'green'
//       case 'in-progress': return 'yellow'
//       case 'scheduled': return 'blue'
//       default: return 'gray'
//     }
//   }

//   const getStatusText = (status: string) => {
//     switch(status) {
//       case 'completed': return '완료'
//       case 'in-progress': return '진행중'
//       case 'scheduled': return '예정'
//       default: return '알 수 없음'
//     }
//   }

//   const getPriorityColor = (priority: string) => {
//     switch(priority) {
//       case 'high': return 'red'
//       case 'medium': return 'orange'
//       case 'low': return 'green'
//       default: return 'gray'
//     }
//   }

//   return (
//     <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
//       <Dialog.Backdrop />
//       <Dialog.Positioner>
//         <Dialog.Content maxW="7xl" mx={4}>
//           <Dialog.Header>
//             <Dialog.Title>정비 내역 조회</Dialog.Title>
//             <Dialog.CloseTrigger />
//           </Dialog.Header>

//           <Dialog.Body>
//             <Box overflowX="auto">
//               <Table.Root variant="outline" size="sm">
//                 <Table.Header>
//                   <Table.Row>
//                     <Table.ColumnHeader>날짜</Table.ColumnHeader>
//                     <Table.ColumnHeader>장비명</Table.ColumnHeader>
//                     <Table.ColumnHeader>코드</Table.ColumnHeader>
//                     <Table.ColumnHeader>작업 내용</Table.ColumnHeader>
//                     <Table.ColumnHeader>비용</Table.ColumnHeader>
//                     <Table.ColumnHeader>사용 부품</Table.ColumnHeader>
//                     <Table.ColumnHeader>부품 기간</Table.ColumnHeader>
//                     <Table.ColumnHeader>담당자</Table.ColumnHeader>
//                     <Table.ColumnHeader>우선순위</Table.ColumnHeader>
//                     <Table.ColumnHeader>상태</Table.ColumnHeader>
//                   </Table.Row>
//                 </Table.Header>
//                 <Table.Body>
//                   {records.map((record) => (
//                     <Table.Row key={record.id}>
//                       <Table.Cell>{record.date}</Table.Cell>
//                       <Table.Cell>{record.equipmentName}</Table.Cell>
//                       <Table.Cell>
//                         <Badge variant="outline" colorScheme="blue">
//                           {record.code}
//                         </Badge>
//                       </Table.Cell>
//                       <Table.Cell maxW="200px">
//                         <Text fontSize="sm" noOfLines={2}>
//                           {record.description}
//                         </Text>
//                       </Table.Cell>
//                       <Table.Cell>₩{record.cost}만</Table.Cell>
//                       <Table.Cell>{record.partsUsed || '-'}</Table.Cell>
//                       <Table.Cell>{record.partsDuration || '-'}</Table.Cell>
//                       <Table.Cell>{record.technician || '-'}</Table.Cell>
//                       <Table.Cell>
//                         <Badge colorScheme={getPriorityColor(record.priority)}>
//                           {record.priority.toUpperCase()}
//                         </Badge>
//                       </Table.Cell>
//                       <Table.Cell>
//                         <Badge colorScheme={getStatusColor(record.status)}>
//                           {getStatusText(record.status)}
//                         </Badge>
//                       </Table.Cell>
//                     </Table.Row>
//                   ))}
//                 </Table.Body>
//               </Table.Root>

//               {records.length === 0 && (
//                 <Box textAlign="center" py={8}>
//                   <Text color="gray.500">등록된 정비 내역이 없습니다.</Text>
//                 </Box>
//               )}
//             </Box>
//           </Dialog.Body>
//         </Dialog.Content>
//       </Dialog.Positioner>
//     </Dialog.Root>
//   )
// }

function PredictiveMaintenanceDashboard({
  equipmentStatus: _equipmentStatus = [],
}) {
  const [maintenanceRecords, setMaintenanceRecords] = useState<
    MaintenanceRecord[]
  >([]);
  const [selectedEquipment, setSelectedEquipment] = useState<any>(null);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // 컴포넌트 마운트 시 로컬스토리지에서 데이터 로드
  useEffect(() => {
    const savedRecords = localStorage.getItem("maintenanceRecords");
    if (savedRecords) {
      setMaintenanceRecords(JSON.parse(savedRecords));
    } else {
      // 초기 샘플 데이터
      const sampleRecords: MaintenanceRecord[] = [
        {
          id: "1",
          equipmentId: 1,
          equipmentName: "CNC 밀링머신 #1",
          date: "2025-01-15",
          code: "PM-001",
          description: "정기 윤활 및 그리스 보충",
          cost: 35,
          partsUsed: "리튬 그리스, 윤활유",
          partsDuration: "3개월",
          technician: "서동욱",
          priority: "medium",
          status: "completed",
          estimatedHours: 2,
          actualHours: 2,
        },
        {
          id: "2",
          equipmentId: 3,
          equipmentName: "5축 머시닝센터 #2",
          date: "2025-01-28",
          code: "CM-002",
          description: "유압 시스템 누유 수리",
          cost: 150,
          partsUsed: "유압 호스, O링 세트",
          partsDuration: "1년",
          technician: "박성모",
          priority: "high",
          status: "completed",
          estimatedHours: 4,
          actualHours: 5,
        },
        {
          id: "3",
          equipmentId: 8,
          equipmentName: "CNC 그라인더 #1",
          date: "2025-02-10",
          code: "PM-004",
          description: "베어링 점검 및 교체",
          cost: 85,
          partsUsed: "볼 베어링 세트",
          partsDuration: "8개월",
          technician: "조현건",
          priority: "medium",
          status: "completed",
          estimatedHours: 3,
          actualHours: 3,
        },
        {
          id: "4",
          equipmentId: 5,
          equipmentName: "터닝센터 #3",
          date: "2025-02-22",
          code: "CM-003",
          description: "전기 회로 단락 수리",
          cost: 95,
          partsUsed: "릴레이, 퓨즈",
          partsDuration: "2년",
          technician: "조현건",
          priority: "high",
          status: "completed",
          estimatedHours: 3,
          actualHours: 4,
        },
        {
          id: "5",
          equipmentId: 2,
          equipmentName: "드릴링머신 #4",
          date: "2025-03-08",
          code: "PM-003",
          description: "필터 교체 및 청소",
          cost: 28,
          partsUsed: "에어필터, 오일필터",
          partsDuration: "4개월",
          technician: "김민지",
          priority: "low",
          status: "completed",
          estimatedHours: 1,
          actualHours: 1,
        },
        {
          id: "6",
          equipmentId: 7,
          equipmentName: "프레스기 #1",
          date: "2025-03-15",
          code: "PV-001",
          description: "진동 분석 및 밸런싱",
          cost: 120,
          partsUsed: "밸런싱 웨이트",
          partsDuration: "6개월",
          technician: "한지원",
          priority: "medium",
          status: "completed",
          estimatedHours: 4,
          actualHours: 4,
        },
        {
          id: "7",
          equipmentId: 4,
          equipmentName: "용접로봇 #2",
          date: "2025-03-28",
          code: "UP-003",
          description: "자동화 시스템 개선",
          cost: 280,
          partsUsed: "센서 모듈, 제어기",
          partsDuration: "2년",
          technician: "공영덕",
          priority: "medium",
          status: "completed",
          estimatedHours: 8,
          actualHours: 9,
        },
        {
          id: "8",
          equipmentId: 5,
          equipmentName: "터닝센터 #3",
          date: "2025-07-08",
          code: "PM-001",
          description: "정기 윤활 및 그리스 보충",
          cost: 38,
          partsUsed: "합성 윤활유",
          partsDuration: "3개월",
          technician: "서동욱",
          priority: "medium",
          status: "in-progress",
          estimatedHours: 2,
          actualHours: undefined,
        },
        {
          id: "9",
          equipmentId: 7,
          equipmentName: "프레스기 #1",
          date: "2025-07-12",
          code: "UP-001",
          description: "CNC 소프트웨어 업데이트",
          cost: 120,
          partsUsed: "소프트웨어 라이센스",
          partsDuration: "3년",
          technician: "김민지",
          priority: "medium",
          status: "scheduled",
          estimatedHours: 4,
          actualHours: undefined,
        },
        {
          id: "10",
          equipmentId: 9,
          equipmentName: "레이저커팅기 #1",
          date: "2025-07-15",
          code: "PV-004",
          description: "정렬 상태 점검",
          cost: 65,
          partsUsed: "정렬 도구",
          partsDuration: "1년",
          technician: "노준혁",
          priority: "low",
          status: "scheduled",
          estimatedHours: 3,
          actualHours: undefined,
        },
      ];
      setMaintenanceRecords(sampleRecords);
      localStorage.setItem("maintenanceRecords", JSON.stringify(sampleRecords));
    }
  }, []);

  // 정비 기록 저장 함수
  const saveMaintenanceRecord = (record: MaintenanceRecord) => {
    const updatedRecords = [...maintenanceRecords, record];
    setMaintenanceRecords(updatedRecords);
    localStorage.setItem("maintenanceRecords", JSON.stringify(updatedRecords));
  };

  // 정비 스케줄 등록 핸들러
  const handleScheduleClick = (equipment: any) => {
    setSelectedEquipment(equipment);
    setIsScheduleOpen(true);
  };

  // 정적 데이터로 변경
  const maintenanceSchedule = [
    {
      equipmentId: 3,
      equipmentName: "5축 머시닝센터 #2",
      priority: "high",
      predictedFailureDate: "2025-07-12",
      confidence: 78,
      estimatedDowntime: "4시간",
      estimatedCost: "150만원",
      recommendedActions: ["냉각수 교체", "펌프 점검", "온도센서 교체"],
      currentRisk: 78,
    },
    {
      equipmentId: 8,
      equipmentName: "CNC 그라인더 #1",
      priority: "medium",
      predictedFailureDate: "2025-07-15",
      confidence: 65,
      estimatedDowntime: "2시간",
      estimatedCost: "80만원",
      recommendedActions: ["베어링 교체", "정렬 조정"],
      currentRisk: 45,
    },
    {
      equipmentId: 1,
      equipmentName: "CNC 밀링머신 #1",
      priority: "low",
      predictedFailureDate: "2025-07-25",
      confidence: 55,
      estimatedDowntime: "1시간",
      estimatedCost: "30만원",
      recommendedActions: ["정기 윤활", "필터 교체"],
      currentRisk: 25,
    },
  ];

  // ROI 데이터
  const roiData = {
    annualSavings: 2400,
    downtimeReduction: 85,
    costAvoidance: 1800,
    maintenanceCostReduction: 35,
  };

  // 월별 절약 효과 데이터
  const monthlySavings = [
    { month: "1월", savings: 180 },
    { month: "2월", savings: 220 },
    { month: "3월", savings: 195 },
    { month: "4월", savings: 240 },
    { month: "5월", savings: 280 },
    { month: "6월", savings: 260 },
  ];

  // 장비별 위험도 분포
  const riskDistribution = [
    { name: "낮음 (0-30%)", value: 12, color: "#38a169" },
    { name: "보통 (31-60%)", value: 4, color: "#dd6b20" },
    { name: "높음 (61-100%)", value: 2, color: "#e53e3e" },
  ];

  // AI 예측 정확도 트렌드
  const accuracyTrend = [
    { month: "1월", accuracy: 78 },
    { month: "2월", accuracy: 82 },
    { month: "3월", accuracy: 85 },
    { month: "4월", accuracy: 88 },
    { month: "5월", accuracy: 91 },
    { month: "6월", accuracy: 89 },
  ];

  // 정비 기록 상태 업데이트 함수
  const updateMaintenanceStatus = (
    id: string,
    status: "scheduled" | "in-progress" | "completed"
  ) => {
    const updatedRecords = maintenanceRecords.map((record) =>
      record.id === id ? { ...record, status } : record
    );
    setMaintenanceRecords(updatedRecords);
    localStorage.setItem("maintenanceRecords", JSON.stringify(updatedRecords));
  };

  // 정비 기록 삭제 함수
  const deleteMaintenanceRecord = (id: string) => {
    const updatedRecords = maintenanceRecords.filter(
      (record) => record.id !== id
    );
    setMaintenanceRecords(updatedRecords);
    localStorage.setItem("maintenanceRecords", JSON.stringify(updatedRecords));
  };

  return (
    <VStack gap={8} align="stretch">
      {/* AI 성능 요약 */}
      <Grid templateColumns="repeat(4, 1fr)" gap={6}>
        <CustomCard bg="#F5F5F5" borderColor="transparent">
          <HStack gap={5} align="center" h="150px" px={6}>
            <Circle size="100px" bg="white">
              <Box
                as="img"
                src="/assets/dashboard/fi5.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
                {...({} as any)}
              />
            </Circle>
            <VStack align="flex-start" gap={1}>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color="#1A1A1A"
                fontFamily="Noto Sans KR"
              >
                AI 예측 정확도
              </Text>
              <HStack align="flex-end" gap={0}>
                <Text
                  fontSize="4xl"
                  fontWeight="bold"
                  color="#2A72F7"
                  fontFamily="Noto Sans KR"
                >
                  89
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color="#1A1A1A"
                  fontFamily="Noto Sans KR"
                  pb={1}
                >
                  %
                </Text>
              </HStack>
              <Text fontSize="md" color="#1A1A1A" fontFamily="Noto Sans KR">
                지난 6개월 평균
              </Text>
            </VStack>
          </HStack>
        </CustomCard>

        <CustomCard bg="#F5F5F5" borderColor="transparent">
          <HStack gap={5} align="center" h="150px" px={6}>
            <Circle size="100px" bg="white">
              <Box
                as="img"
                src="/assets/dashboard/fi7.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
                {...({} as any)}
              />
            </Circle>
            <VStack align="flex-start" gap={1}>
              <Text
                fontSize="xl"
                fontWeight="bold"
                color="#1A1A1A"
                fontFamily="Noto Sans KR"
              >
                연간 절약 예상
              </Text>
              <HStack align="flex-end" gap={0}>
                <Text
                  fontSize="4xl"
                  fontWeight="bold"
                  color="#1A1A1A"
                  fontFamily="Noto Sans KR"
                >
                  {roiData.annualSavings}
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color="#1A1A1A"
                  fontFamily="Noto Sans KR"
                  pb={1}
                >
                  만
                </Text>
              </HStack>
              <Text fontSize="md" color="#1A1A1A" fontFamily="Noto Sans KR">
                예지보전 효과
              </Text>
            </VStack>
          </HStack>
        </CustomCard>

        <CustomCard bg="#F5F5F5" borderColor="transparent">
          <HStack gap={5} align="center" h="150px" px={6}>
            <Circle size="100px" bg="white">
              <Box
                as="img"
                src="/assets/dashboard/fi8.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
                {...({} as any)}
              />
            </Circle>
            <VStack align="flex-start" gap={1}>
              <Text
                fontSize="lx"
                fontWeight="bold"
                color="#1A1A1A"
                fontFamily="Noto Sans KR"
              >
                다운타임 감소
              </Text>
              <HStack align="flex-end" gap={0}>
                <Text
                  fontSize="4xl"
                  fontWeight="bold"
                  color="#2A72F7"
                  fontFamily="Noto Sans KR"
                >
                  {roiData.downtimeReduction}
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color="#1A1A1A"
                  fontFamily="Noto Sans KR"
                  pb={1}
                >
                  %
                </Text>
              </HStack>
              <Text fontSize="md" color="#1A1A1A" fontFamily="Noto Sans KR">
                작년 대비
              </Text>
            </VStack>
          </HStack>
        </CustomCard>

        <CustomCard bg="#F5F5F5" borderColor="transparent">
          <HStack gap={5} align="center" h="150px" px={6}>
            <Circle size="100px" bg="white">
              <Box
                as="img"
                src="/assets/dashboard/fi6.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
                {...({} as any)}
              />
            </Circle>
            <VStack align="flex-start" gap={1}>
              <Text
                fontSize="lx"
                fontWeight="bold"
                color="#1A1A1A"
                fontFamily="Noto Sans KR"
              >
                예정된 정비
              </Text>
              <HStack align="flex-end" gap={0}>
                <Text
                  fontSize="4xl"
                  fontWeight="bold"
                  color="#DC3545"
                  fontFamily="Noto Sans KR"
                >
                  {maintenanceSchedule.length}
                </Text>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  color="#1A1A1A"
                  fontFamily="Noto Sans KR"
                  pb={1}
                >
                  건
                </Text>
              </HStack>
              <Text fontSize="md" color="#1A1A1A" fontFamily="Noto Sans KR">
                향후 2주간
              </Text>
            </VStack>
          </HStack>
        </CustomCard>
      </Grid>

      {/* 정비 내역 조회 버튼 */}
      <HStack justify="adjusted" align="center">
        <Heading size="xl">AI 예지보전 스케줄</Heading>
        <Button
          colorScheme="blue"
          variant="outline"
          onClick={() => setIsHistoryOpen(true)}
        >
          <HStack gap={2}>
            <Text>📋</Text>
            <Text>정비 내역 조회</Text>
          </HStack>
        </Button>
      </HStack>

      {/* 메인 콘텐츠 그리드 */}
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={8}>
        {/* 예지보전 스케줄 */}
        <CustomCard bg="#F5F5F5">
          <VStack gap={6} align="stretch">
            {maintenanceSchedule.map((item) => (
              <Box
                key={item.equipmentId}
                p={5}
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
              >
                <VStack align="stretch" gap={4}>
                  {/* 헤더 */}
                  <HStack justify="space-between" align="start">
                    <VStack align="start" gap={1}>
                      <Text fontWeight="bold" fontSize="lg">
                        {item.equipmentName}
                      </Text>
                      <Badge
                        colorScheme={
                          item.priority === "high"
                            ? "red"
                            : item.priority === "medium"
                              ? "orange"
                              : "blue"
                        }
                        size="md"
                      >
                        {item.priority.toUpperCase()} 위험도
                      </Badge>
                    </VStack>

                    <VStack align="end" gap={1}>
                      <Text fontSize="md" color="gray.500">
                        AI 신뢰도
                      </Text>
                      <Badge
                        bgColor={
                          item.confidence >= 70
                            ? "red.500"
                            : item.confidence >= 60
                              ? "orange.500"
                              : "green.500"
                        }
                        variant="solid"
                        fontSize="md"
                      >
                        {item.confidence}%
                      </Badge>
                    </VStack>
                  </HStack>

                  {/* 위험도 바 */}
                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="md" color="gray.600">
                        현재 고장 위험도
                      </Text>
                      <Text
                        fontSize="md"
                        fontWeight="bold"
                        color={
                          item.currentRisk > 70
                            ? "red.500"
                            : item.currentRisk > 40
                              ? "orange.500"
                              : "green.500"
                        }
                      >
                        {item.currentRisk}%
                      </Text>
                    </HStack>
                    <CustomProgress
                      value={item.currentRisk}
                      colorScheme={
                        item.currentRisk > 70
                          ? "red"
                          : item.currentRisk > 40
                            ? "orange"
                            : "green"
                      }
                      size="lg"
                    />
                  </Box>

                  {/* 상세 정보 */}
                  <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                    <Box>
                      <Text fontSize="md" color="gray.500">
                        예상 고장일
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {item.predictedFailureDate}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="md" color="gray.500">
                        예상 중단시간
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {item.estimatedDowntime}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="md" color="gray.500">
                        예상 비용
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {item.estimatedCost}
                      </Text>
                    </Box>
                  </Grid>

                  {/* 권장 작업 */}
                  <Box>
                    <Text fontSize="md" color="gray.500" mb={2}>
                      🔧 AI 권장 작업
                    </Text>
                    <HStack gap={2} flexWrap="wrap">
                      {item.recommendedActions.map((action) => (
                        <Badge
                          key={action}
                          variant="outline"
                          colorScheme="blue"
                          fontSize="md"
                          p={1}
                        >
                          {action}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>

                  {/* 액션 버튼 */}
                  <HStack gap={3}>
                    <Button
                      size="md"
                      colorScheme="blue"
                      variant="solid"
                      flex={1}
                      onClick={() => handleScheduleClick(item)}
                    >
                      📅 정비 스케줄 등록
                    </Button>
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme="gray"
                      flex={1}
                    >
                      📊 상세 분석
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            ))}

            <Button colorScheme="blue" variant="outline" size="lg">
              + 새 예측 분석 실행
            </Button>
          </VStack>
        </CustomCard>

        {/* 사이드바 - 분석 차트들 */}
        <VStack gap={6} align="stretch">
          {/* 장비 위험도 분포 */}
          <CustomCard bg="#F5F5F5">
            <Heading size="lg" mb={4}>
              장비 위험도 분포
            </Heading>
            <SimpleDonutChart data={riskDistribution} />
          </CustomCard>

          {/* AI 예측 정확도 트렌드 */}
          <CustomCard bg="#F5F5F5">
            <Heading size="lg" mb={4}>
              AI 정확도 트렌드
            </Heading>
            <SimpleLineChart
              data={accuracyTrend}
              dataKey="accuracy"
              color="#3182ce"
            />
          </CustomCard>

          {/* 월별 절약 효과 */}
          <CustomCard>
            <Heading size="lg" mb={4}>
              월별 비용 절약
            </Heading>
            <SimpleBarChart
              data={monthlySavings}
              dataKey="savings"
              color="gray.500"
            />
          </CustomCard>
        </VStack>
      </Grid>

      {/* ROI 상세 분석 */}
      <CustomCard bg="#F5F5F5" borderColor="green.200">
        <Heading size="lg" mb={6}>
          예지보전 ROI 분석
        </Heading>
        <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={8}>
          <VStack gap={4} align="stretch">
            <Heading size="md">비용 효과</Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Box
                textAlign="center"
                p={4}
                bg="white"
                borderRadius="lg"
                shadow="md"
              >
                <Text fontSize="lg" fontWeight="bold">
                  ₩{roiData.annualSavings}만
                </Text>
                <Text fontSize="md" color="gray.600">
                  연간 총 절약
                </Text>
              </Box>
              <Box
                textAlign="center"
                p={4}
                bg="white"
                borderRadius="lg"
                shadow="md"
              >
                <Text fontSize="lg" fontWeight="bold">
                  ₩{roiData.costAvoidance}만
                </Text>
                <Text fontSize="md" color="gray.600">
                  긴급수리 방지
                </Text>
              </Box>
            </Grid>

            <Box p={4} bg="white" borderRadius="lg" shadow="md">
              <Text fontSize="md" fontWeight="medium" mb={2} color="gray.700">
                정비비용 절감
              </Text>
              <HStack>
                <CustomProgress
                  value={roiData.maintenanceCostReduction}
                  colorScheme="green"
                  size="lg"
                />
                <Text fontSize="md" fontWeight="bold" minW="40px">
                  {roiData.maintenanceCostReduction}%
                </Text>
              </HStack>
            </Box>
          </VStack>

          <VStack gap={4} align="stretch">
            <Heading size="md">생산성 향상</Heading>
            <Box p={4} bg="white" borderRadius="lg" shadow="sm">
              <VStack gap={3}>
                <HStack justify="space-between" w="100%">
                  <Text fontSize="md" color="gray.600">
                    계획되지 않은 중단시간
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="blue.600">
                    -{roiData.downtimeReduction}%
                  </Text>
                </HStack>
                <CustomProgress
                  value={roiData.downtimeReduction}
                  max={100}
                  size="lg"
                />
                <Text fontSize="md" color="gray.500">
                  작년 대비 개선율
                </Text>
              </VStack>
            </Box>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Box
                textAlign="center"
                p={4}
                bg="white"
                borderRadius="lg"
                shadow="sm"
              >
                <Text fontSize="lg" fontWeight="bold">
                  96.5%
                </Text>
                <Text fontSize="md" color="gray.600">
                  가동률
                </Text>
              </Box>
              <Box
                textAlign="center"
                p={4}
                bg="white"
                borderRadius="lg"
                shadow="sm"
              >
                <Text fontSize="lg" fontWeight="bold">
                  2.3일
                </Text>
                <Text fontSize="md" color="gray.600">
                  평균 MTBF
                </Text>
              </Box>
            </Grid>
          </VStack>
        </Grid>
      </CustomCard>

      {/* 정비 스케줄 등록 모달 */}
      <MaintenanceScheduleModal
        isOpen={isScheduleOpen}
        onClose={() => setIsScheduleOpen(false)}
        equipmentData={selectedEquipment}
        onSave={saveMaintenanceRecord}
      />

      {/* 정비 내역 조회 모달 */}
      <MaintenanceHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        records={maintenanceRecords}
        onUpdateStatus={updateMaintenanceStatus}
        onDeleteRecord={deleteMaintenanceRecord}
      />
    </VStack>
  );
}

export default PredictiveMaintenanceDashboard;
